// What the sign-in page offers besides the password: "Continue with Google"
// and "New to Corvus? Create a workspace", each only when the api says so
// (GET /api/auth/options) and never inside the iPhone app or the Mac app:
// Google refuses its sign-in inside an embedded web view.

import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";

import { fetchAuthOptions, googleStartUrl, type AuthOptions } from "@/api/signup";
import { buttonClasses } from "@/components/ui/Button";
import { isMacShell } from "@/native/desktop";
import { isEmbedded } from "@/native/embedded";

import { GoogleMark } from "./AuthFrame";

const NONE: AuthOptions = { google: false, signup: false };

/** The two offers; both off until the api has answered, and always off in the apps. */
export function useAuthOptions(): AuthOptions {
  const [options, setOptions] = useState<AuthOptions>(NONE);
  useEffect(() => {
    if (isEmbedded() || isMacShell()) return;
    const ctl = new AbortController();
    void fetchAuthOptions(ctl.signal).then((o) => !ctl.signal.aborted && setOptions(o));
    return () => ctl.abort();
  }, []);
  return options;
}

/** A full-width "Continue with Google" link (a plain link: the api answers with Google's page). */
export function GoogleButton({ href, label = "Continue with Google" }: { href: string; label?: string }) {
  return (
    <a href={href} className={buttonClasses({ variant: "secondary", size: "lg", className: "w-full" })}>
      <GoogleMark />
      {label}
    </a>
  );
}

/** "or", then Continue with Google: under the password form; what Google's sign-in said comes back under it. */
export function GoogleSignIn({ error }: { error?: string | null }) {
  return (
    <div className="mt-5 flex flex-col gap-5">
      <div className="flex items-center gap-3 text-xs text-fg-subtle" aria-hidden>
        <span className="flex-1 border-t border-line-subtle" />
        or
        <span className="flex-1 border-t border-line-subtle" />
      </div>
      <div className="flex flex-col gap-3">
        <GoogleButton href={googleStartUrl("signin")} />
        {error && (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger [text-wrap:pretty]">
            <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

export function NewWorkspaceLine() {
  return (
    <p className="mt-6 text-center text-sm text-fg-muted">
      New to Corvus?{" "}
      <a href="/signup" className="font-medium text-accent-fg hover:underline">
        Create a workspace
      </a>
    </p>
  );
}
